import { ApolloError } from "@apollo/client/core";
import { useMutation, useApolloClient } from "@vue/apollo-composable";
import { createSharedComposable, computedEager, computedAsync, useLastChanged } from "@vueuse/core";
import { sumBy, difference, keyBy, merge } from "lodash";
import { computed, nextTick, readonly, ref, watch } from "vue";
import { AbortReason } from "@/core/api/common/enums";
import {
  useGetShortCartQuery,
  useChangeShortCartItemQuantityMutation,
  useGetFullCartQuery,
  getMetricReplacementItemIds,
  useValidateCoupon,
  generateCacheIdIfNew,
  getCarts,
  useRemoveCartMutation,
  DEFAULT_CART_NAME,
  logClientInformation,
} from "@/core/api/graphql";
import { useAddOrUpdateCartPaymentWithTokenValidationMutation } from "@/core/api/graphql/cart/mutations/addOrUpdateCartPaymentWithTokenValidation";
import { useConvertCartMutation } from "@/core/api/graphql/cart/mutations/convertCart";
import { useRefreshCartItemsPriceMutation } from "@/core/api/graphql/cart/mutations/refreshCartItemsPrice";
import { useReorderCartItemsMutation } from "@/core/api/graphql/cart/mutations/reorderCartItems";
import { saveCart } from "@/core/api/graphql/cart/mutations/saveCart";
import { useUpdateAccessoryMutation } from "@/core/api/graphql/cart/mutations/updateAccessory";
import { useUpdateCartItemDynamicPropertiesMutation } from "@/core/api/graphql/cart/mutations/updateCartItemDynamicProperties/updateCartiItemDynamicProperties";
import {
  AddItemDocument,
  AddItemsCartDocument,
  AddBulkItemsCartDocument,
  SelectCartItemsDocument,
  UnselectCartItemsDocument,
  ClearCartDocument,
  RemoveCartItemsDocument,
  ChangeFullCartItemsQuantityDocument,
  AddCouponDocument,
  RemoveCouponDocument,
  ChangeCartCommentDocument,
  ChangePurchaseOrderNumberDocument,
  AddOrUpdateCartShipmentDocument,
  RemoveShipmentDocument,
  AddOrUpdateCartPaymentDocument,
  AddGiftItemsDocument,
  RejectGiftItemsDocument,
  ChangeFullCartItemQuantityDocument,
  GetFullCartDocument,
} from "@/core/api/graphql/types";
import { useCurrency } from "@/core/composables";
import { getMergeStrategyUniqueBy, useMutationBatcher } from "@/core/composables/useMutationBatcher";
import { ProductType, ValidationErrorObjectType } from "@/core/enums";
import { globals } from "@/core/globals";
import { groupByVendor, Logger } from "@/core/utilities";
import { createSharedComposableByArgs } from "@/core/utilities/composables/createSharedComposableByArgs";
import { useThorlabsAlgoliaAnalytics } from "@/shared/algolia-analytics/algolia-analytics";
import { currencyConfig } from "@/shared/checkout/currency-settings";
import { gtmSendAddToCartEvent, gtmSendRemoveFromCartEvent } from "@/shared/gtm/gtm-cart-events";
import { useModal } from "@/shared/modal";
import { useNotifications } from "@/shared/notification";
import ClearCartModal from "../components/clear-cart-modal.vue";
import { CartValidationErrors } from "../enums";
import { getAvailability, getAlgoliaDynamicProperties, algoliaDynamicPropertyName } from "../utils";
import type { CartSearchParam } from "../types/search";
import type {
  InputNewBulkItemType,
  InputNewCartItemType,
  ShortCartFragment,
  CartExtension,
  InputPaymentTypeExtension,
  InputShipmentTypeExtension,
  AddOrUpdateCartShipmentMutation,
  AddOrUpdateCartShipmentMutationVariables,
  CartExtensionConnection,
  InputAddItemsTypeExtension,
  InputCustomerReferenceType,
  LineItemTypeExtension,
  ConvertCartType,
  SaveCartType,
  ConfigurationSectionInputTl,
  InputUpdateAccessoryType,
  AddOrUpdateCartPaymentWithTokenValidationMutation,
  AddOrUpdateCartPaymentWithTokenValidationMutationVariables,
  AuthorizePaymentResultType,
} from "@/core/api/graphql/types";
import type { OutputBulkItemType, ExtendedGiftItemType, LineItemWithAvailabilityStatus } from "@/shared/cart/types";
import type { CurrencySettingsType } from "@/shared/checkout";

const lastAddedItemNumber = ref<LineItemTypeExtension>();
const lastRemovedLineItem = ref<LineItemTypeExtension>();
const { currentCurrency } = useCurrency();

/**
 * Reactive shared access to the current cart state using the GetFullCart GraphQL query.
 *
 * Notes:
 * - Ensure that any mutation which modifies the cart returns a CartType object
 *   matching the shape of the GetFullCart query result.
 * - If a new mutation is added, explicitly test the scenario where the cart is initially null.
 *   This is common in lazy-initialization flows where the cart is created only upon user action.
 * - Consumers of `cart` should gracefully handle `null` values to avoid rendering errors.
 */
function _useSharedShortCart() {
  const { result: query, refetch, loading } = useGetShortCartQuery();
  const cart = computed(() => query.value?.cart);

  return {
    cart,
    refetch,
    loading,
  };
}

const useSharedShortCart = createSharedComposable(_useSharedShortCart);

export function useShortCart() {
  const { cart, refetch, loading } = useSharedShortCart();
  const { storeId, currencyCode, cultureName, userId } = globals;
  const commonVariables = { storeId, currencyCode, cultureName, userId };
  const { mutate: _addToCart, loading: addToCartLoading } = useMutation(AddItemDocument);

  async function addToCart(
    productId: string,
    quantity: number,
    serialNumberTL: string | undefined = undefined,
    itemNumberTL: string | undefined = undefined,
    customDescriptionTL: string | undefined = undefined,
    configurationSectionsTL: ConfigurationSectionInputTl[] | undefined = undefined,
  ) {
    const dynamicProperties = getAlgoliaDynamicProperties();

    const { isFromAlgoliaSearch, algoliaSendAddedToCartObjectIDsAfterSearch } = useThorlabsAlgoliaAnalytics();

    try {
      const lineItemsIDs = cart.value?.items.map((item) => item.id) ?? [];
      const result = await _addToCart(
        {
          command: {
            productId,
            quantity,
            serialNumberTL,
            itemNumberTL,
            customDescriptionTL,
            configurationSectionsTL,
            dynamicProperties,
            ...commonVariables,
          },
        },
        {
          update: (cache, { data }) => {
            if (data?.addItem) {
              // Write the new cart to the cache for the GetFullCart query
              cache.writeQuery({
                query: GetFullCartDocument,
                data: { cart: data.addItem },
                variables: commonVariables,
              });
            }
          },
        },
      );

      if (result?.data?.addItem) {
        lastAddedItemNumber.value = result.data.addItem.items.find(
          (item) => !lineItemsIDs.includes(item.id),
        ) as LineItemTypeExtension;

        //Trigger algolia analytics event when items is added to cart
        if (isFromAlgoliaSearch.value && lastAddedItemNumber.value) {
          await algoliaSendAddedToCartObjectIDsAfterSearch([lastAddedItemNumber.value as LineItemTypeExtension]);
        }

        //Trigger GTM add to cart event
        if (lastAddedItemNumber.value) {
          gtmSendAddToCartEvent([lastAddedItemNumber.value]);
        }
      }

      return result?.data?.addItem;
    } catch (err) {
      if (err instanceof ApolloError && err.networkError?.toString() === (AbortReason.Explicit as string)) {
        return;
      }
      Logger.error(err as string);
    }
  }

  const { mutate: _addItemsToCart, loading: addItemsToCartLoading } = useMutation(AddItemsCartDocument);
  async function addItemsToCart(
    items: InputNewCartItemType[],
    cartName: string = "",
    cartId: string = "",
    customerReferences: InputCustomerReferenceType[] = [],
  ) {
    const payload: InputAddItemsTypeExtension = {
      cartItems: items,
      customerReferences,
      ...commonVariables,
    };
    // InputCustomerReferenceType
    if (cartName.length > 0) {
      payload.cartName = cartName;
    }

    if (cartId.length > 0) {
      payload.cartId = cartId;
    }

    const {
      isFromAlgoliaSearch,
      currentAlgoliaQueryID,
      currentAlgoliaQueryIndex,
      algoliaSendAddedToCartObjectIDsAfterSearch,
    } = useThorlabsAlgoliaAnalytics();

    //Remember existing line items so we can get only new ones later
    const existingLineItemsIDs = cart.value?.items.map((item) => item.id) ?? [];

    //Store Algolia queryID and index name as dynamic properties on line items
    if (isFromAlgoliaSearch.value) {
      payload.cartItems.forEach((item) => {
        if (!item.dynamicProperties) {
          item.dynamicProperties = [];
        }

        item.dynamicProperties.push({
          name: algoliaDynamicPropertyName,
          value: `${currentAlgoliaQueryID.value}~${currentAlgoliaQueryIndex.value}`,
        });
      });
    }

    const result = await _addItemsToCart(
      { command: payload },
      {
        update: (cache, { data }) => {
          if (data?.addItemsCart) {
            cache.writeQuery({
              query: GetFullCartDocument,
              data: { cart: data.addItemsCart },
              variables: commonVariables,
            });
          }
        },
      },
    );

    const newLineItems = (result?.data?.addItemsCart?.items.filter((item) => !existingLineItemsIDs.includes(item.id)) ??
      []) as LineItemTypeExtension[] | undefined;

    if (!!newLineItems && newLineItems.length > 0) {
      if (isFromAlgoliaSearch.value) {
        await algoliaSendAddedToCartObjectIDsAfterSearch(newLineItems);
      }

      //Trigger GTM add to cart event
      gtmSendAddToCartEvent(newLineItems);
    }

    return result?.data?.addItemsCart;
  }

  const { mutate: _changeItemQuantity, loading: changeItemQuantityLoading } = useChangeShortCartItemQuantityMutation();
  async function changeItemQuantity(lineItemId: string, quantity: number): Promise<ShortCartFragment | undefined> {
    const result = await _changeItemQuantity({ command: { lineItemId, quantity } });
    return result?.data?.changeCartItemQuantity;
  }

  function getItemsTotal(productIds: string[]): number {
    if (!cart.value?.items.length) {
      return 0;
    }

    const filteredItems = cart.value.items.filter((item) => productIds.includes(item.productId));

    return sumBy(filteredItems, (x) => x.extendedPrice.amount);
  }

  return {
    cart,
    refetch,
    addToCart,
    addItemsToCart,
    changeItemQuantity,
    getItemsTotal,
    loading,
    changing: computed(() => addToCartLoading.value || addItemsToCartLoading.value || changeItemQuantityLoading.value),
  };
}

export function _useFullCart(cartId?: string, cartName?: string) {
  const { openModal } = useModal();
  const { resolveClient } = useApolloClient();
  const { storeId, currencyCode, cultureName, userId } = globals;
  // When cartId is not provided, use cartName to fetch the cart (defaults to "default")
  const effectiveCartName = cartId ? undefined : cartName ?? DEFAULT_CART_NAME;
  const commonVariables = { storeId, currencyCode, cultureName, userId, cartId, cartName: effectiveCartName };
  const notifications = useNotifications();
  const t = globals.i18n.global.t;
  const { result: query, load, refetch, loading, start } = useGetFullCartQuery(cartId, effectiveCartName);

  const checkoutCart = ref<CartExtension | undefined>(query.value?.cart as CartExtension | undefined);
  const lastChangedCheckoutCartResponse = useLastChanged(checkoutCart, { flush: "sync" });
  const lastChangedCartQuery = useLastChanged(query, { flush: "sync" });
  let initialLoad: Exclude<ReturnType<typeof load>, false> | undefined;
  const forceFetch = async () => {
    if (initialLoad) {
      const pendingLoad = initialLoad;
      // start() before the variables watcher flushes sends empty variables; after it, it restarts a query stopped by its unmounted owner.
      await nextTick();
      start();
      return await pendingLoad;
    }

    start();
    const firstLoad = load();
    if (firstLoad) {
      initialLoad = firstLoad.finally(() => {
        initialLoad = undefined;
      });
      return await initialLoad;
    }

    return await refetch();
  };

  const cart = computed(() => {
    const checkoutCartLastChanged = lastChangedCheckoutCartResponse.value ?? 0;
    const cartQueryLastChanged = lastChangedCartQuery.value ?? 0;

    if (checkoutCartLastChanged > cartQueryLastChanged) {
      return checkoutCart.value;
    }
    return query.value?.cart as CartExtension | undefined;
  });

  const shipment = computed(() => cart.value?.shipments?.[0]);
  const payment = computed(() => cart.value?.payments?.[0]);

  const availableShippingMethods = computed(() => {
    const currencySettings = currencyConfig[globals.currencyCode] as CurrencySettingsType;
    return (
      cart.value?.availableShippingMethodsTL?.filter((method) =>
        currencySettings.availableShippingMethods.includes(method.code),
      ) ?? []
    );
  });

  const availablePaymentMethods = computed(() => {
    const currencySettings = currencyConfig[globals.currencyCode] as CurrencySettingsType;
    return (
      cart.value?.availablePaymentMethods.filter((method) =>
        currencySettings.availablePaymentMethods.includes(method.code),
      ) ?? []
    );
  });

  const lineItemsGroupedByVendor = computed(() => groupByVendor(cart.value?.items ?? []));

  const selectedLineItems = computed(() => cart.value?.items?.filter((item) => item.selectedForCheckout) ?? []);

  const selectedLineItemsGroupedByVendor = computed(() => groupByVendor(selectedLineItems.value));

  const hasOnlyUnselectedLineItems = computedEager(() => selectedLineItems.value.length === 0);

  const allItemsAreDigital = computed(() =>
    selectedLineItems.value.length > 0
      ? selectedLineItems.value.every((item) => item.productType === ProductType.Digital)
      : undefined,
  );

  const addedGiftsByIds = computed(() => keyBy(cart.value?.gifts, "id"));

  const availableExtendedGifts = computed<ExtendedGiftItemType[]>(() =>
    (cart.value?.availableGifts ?? []).map((gift) => ({ ...gift, isAddedInCart: !!addedGiftsByIds.value[gift.id] })),
  );

  const hasValidationErrors = computedEager(
    () =>
      cart.value?.validationErrors?.some(
        (error) =>
          (error.objectType === ValidationErrorObjectType.CartProduct &&
            selectedLineItems.value?.some((item) => item.productId === error.objectId)) ||
          (error.objectType === ValidationErrorObjectType.LineItem &&
            selectedLineItems.value?.some((item) => item.id === error.objectId)),
      ) ?? selectedLineItems.value?.some((item) => item.validationErrors?.length),
  );

  const hasOnlyUnselectedValidationError = computedEager(
    () =>
      cart.value?.validationErrors?.length == 1 &&
      cart.value.validationErrors[0]?.errorCode == CartValidationErrors.ALL_LINE_ITEMS_UNSELECTED,
  );

  // Zero price detection - check for ZERO_PRICE_DETECTED errors
  const zeroPriceLineItemIds = computedEager(() => {
    if (!cart.value?.validationErrors?.length) {
      return [];
    }

    return cart.value.validationErrors
      .filter(
        (error) =>
          error.errorCode === "PRODUCT_PRICE_CHANGED" &&
          error.errorParameters?.find((param) => param.key === "new_price" && param.value === "0"),
      )
      .map((error) => error.objectId)
      .filter((id): id is string => !!id);
  });

  // OURS
  const availabilityItems = computedAsync(async () => {
    if (cart.value == null || cart.value.items.length === 0) {
      return;
    }

    const result = await getAvailability(cart.value.items);

    return cart.value.items.map((item) => {
      let status = result?.find((x) => x.productId === item.id)?.availabilityResponse?.availability ?? "";

      if (status && status === "None") {
        status = t("shared.product.request_lead_time");
      }

      return <LineItemWithAvailabilityStatus>{
        lineItem: item,
        status,
        productTitle: item.product?.mainProductId
          ? item.product?.masterVariation?.slug
          : item.sku.replace("Configuration-", ""),
      };
    });
  });

  const cartWeight = computed(() =>
    cart.value?.items?.reduce((acc, item) => acc + (item.product?.weight ?? 0) * item.quantity, 0),
  );

  const cartHasUkHeavyItems = computed<boolean>(
    () => cart.value?.items?.some((item) => item.product?.ukHvyTL ?? false) ?? false,
  );
  const cartHasVaporCells = computed<boolean>(
    () => cart.value?.items?.some((item) => item.product?.isVaporCellTL ?? false) ?? false,
  );

  const itemsInCart = computed(() => {
    const itemsLength = cart.value ? sumBy(cart.value.items ?? [], (x) => x.quantity) : 0;

    return itemsLength === 1
      ? `(${itemsLength} ${t("shared.cart.item")})`
      : `(${itemsLength} ${t("common.labels.items")})`;
  });

  const itemsInCartWithLines = computed(() => {
    const linesCount = cart.value?.items?.length ?? 0;
    const linesText = linesCount === 1 ? `1 ${t("shared.cart.line")}` : `${linesCount} ${t("shared.cart.lines")}`;

    const itemsText = itemsInCart.value.slice(1, -1);
    return linesCount === 0 ? `${itemsInCart.value}` : `(${itemsText} ${t("common.labels.on_lowercase")} ${linesText})`;
  });

  const lineItemsDiscountTotal = computed(() =>
    sumBy<LineItemTypeExtension>(cart?.value?.items, (item) => item.discountTotal?.amount ?? 0),
  );

  const hasDiscounts = computed(() => {
    return cart?.value?.discounts?.length || lineItemsDiscountTotal.value > 0;
  });

  const hasOnlyNewLabDiscount = computed(() => {
    const itemsWithDiscounts = cart?.value?.items?.filter((item) => item.discounts?.length > 0) ?? [];
    if (itemsWithDiscounts.length === 0) {
      return false;
    }
    return itemsWithDiscounts.every((item) => item.discounts.find((d) => d.description?.toLowerCase() === "nl10"));
  });
  // END OURS

  const { mutate: _selectCartItems, loading: selectCartItemsLoading } = useMutation(SelectCartItemsDocument);
  const { mutate: _unselectCartItemsMutation, loading: unselectCartItemsLoading } =
    useMutation(UnselectCartItemsDocument);
  // MISSING some baching mutations

  // HEAVY changes, not altered
  const selectedItemIds = computed({
    get: () => selectedLineItems.value.map((item) => item.id),
    set: (newValue) => {
      const oldValue = selectedItemIds.value;

      const newlySelectedLineItemIds = difference(newValue, oldValue);
      const newlyUnselectedLineItemIds = difference(oldValue, newValue);

      const hasNewlySelected = newlySelectedLineItemIds.length > 0;
      const hasNewlyUnselected = newlyUnselectedLineItemIds.length > 0;
      if (hasNewlySelected) {
        void _selectCartItems(
          {
            command: {
              lineItemIds: newlySelectedLineItemIds,
              ...commonVariables,
            },
            skipQuery: false,
          },
          {
            optimisticResponse: {
              selectCartItems: merge({}, cart.value!, {
                items: cart.value!.items.map((item) => ({
                  selectedForCheckout: newlySelectedLineItemIds.includes(item.id) || item.selectedForCheckout,
                })),
              }),
            },
          },
        );
      }
      if (hasNewlyUnselected) {
        void _unselectCartItemsMutation(
          {
            command: {
              lineItemIds: newlyUnselectedLineItemIds,
              ...commonVariables,
            },
            skipQuery: false,
          },
          {
            optimisticResponse: {
              unSelectCartItems: merge({}, cart.value!, {
                items: cart.value!.items.map((item) => ({
                  selectedForCheckout: !newlyUnselectedLineItemIds.includes(item.id) && item.selectedForCheckout,
                })),
              }),
            },
          },
        );
      }
    },
  });

  const { mutate: _clearCart, loading: clearCartLoading, onDone: onClearCartDone } = useMutation(ClearCartDocument);

  onClearCartDone(() => {
    resolveClient().cache.gc();
  });

  async function clearCart(skipQuery = false): Promise<void> {
    await _clearCart({ command: { ...commonVariables }, skipQuery: skipQuery });
  }

  const { mutate: _removeItems, loading: removeItemsLoading } = useMutation(RemoveCartItemsDocument);
  async function removeItems(lineItemIds: string[]): Promise<void> {
    // Capture the removed line items before removing them
    const removedLineItems = cart.value?.items.filter((item) => lineItemIds.includes(item.id)) ?? [];

    if (lineItemIds.length === 1) {
      lastRemovedLineItem.value = removedLineItems[0];
    }

    await _removeItems(
      { command: { lineItemIds, ...commonVariables }, skipQuery: false },
      {
        optimisticResponse: {
          removeCartItems: {
            ...cart.value!,
            items: cart.value!.items.filter((item) => !lineItemIds.includes(item.id)),
          },
        },
      },
    );

    // Send GTM event for removed items
    if (removedLineItems.length > 0) {
      gtmSendRemoveFromCartEvent(removedLineItems);
    }
  }

  const { mutate: _changeItemQuantity, loading: changeItemQuantityLoading } = useMutation(
    ChangeFullCartItemQuantityDocument,
  );
  async function changeItemQuantity(lineItemId: string, quantity: number): Promise<void> {
    await _changeItemQuantity({ command: { lineItemId, quantity, ...commonVariables }, skipQuery: false });
  }

  const { mutate: _changeItemsQuantity, loading: changeItemsQuantityLoading } = useMutation(
    ChangeFullCartItemsQuantityDocument,
  );
  const { add, overflowed: changeItemQuantityBatchedOverflowed } = useMutationBatcher(_changeItemsQuantity, {
    mergeStrategy: getMergeStrategyUniqueBy("lineItemId"),
  });
  async function changeItemQuantityBatched(lineItemId: string, quantity: number): Promise<void> {
    try {
      await add({
        command: {
          cartItems: [{ lineItemId, quantity }],
          ...commonVariables,
        },
      });
    } catch (error) {
      if (error instanceof ApolloError && error.networkError?.toString() === (AbortReason.Explicit as string)) {
        return;
      }
      Logger.error(changeItemQuantityBatched.name, error);
    }
  }

  const { mutate: _refreshCartItemsPrice, loading: refreshCartItemsPriceLoading } =
    useRefreshCartItemsPriceMutation(cart);
  async function refreshCartItemsPrice(lineItemIds: string[]): Promise<void> {
    try {
      await _refreshCartItemsPrice({
        command: {
          lineItemIds,
          ...commonVariables,
        },
      });
    } catch (error) {
      if (error instanceof ApolloError && error.networkError?.toString() === (AbortReason.Explicit as string)) {
        return;
      }
      Logger.error(refreshCartItemsPrice.name, error);
    }
  }

  // Auto-refresh cart item prices when zero price errors are detected
  watch(
    zeroPriceLineItemIds,
    async (newZeroPriceItemIds, oldZeroPriceItemIds) => {
      // Only trigger refresh if there are new zero price items and they're different from previous ones
      if (
        newZeroPriceItemIds.length > 0 &&
        JSON.stringify(newZeroPriceItemIds) !== JSON.stringify(oldZeroPriceItemIds)
      ) {
        try {
          await refreshCartItemsPrice(newZeroPriceItemIds);
          Logger.info(`Auto-refreshed prices for ${newZeroPriceItemIds.length} items with zero price errors`);
        } catch (error) {
          Logger.error("Auto-refresh cart items price failed", error);
        }
      }
    },
    { immediate: true },
  );

  // const { mutate: _validateCoupon, loading: validateCouponLoading } = useValidateCoupon("", "", "", "", "", "", "", "");
  async function validateCartCoupon(): Promise<boolean | undefined> {
    // const result = await _validateCoupon({ command: { coupon: couponCode } });
    return useValidateCoupon("", "", "", "", "", "", "", "");
  }

  const { mutate: _addCoupon, loading: addCouponLoading } = useMutation(AddCouponDocument);
  async function addCartCoupon(couponCode: string): Promise<void> {
    await _addCoupon({ command: { couponCode, ...commonVariables }, skipQuery: false });
  }

  const { mutate: _removeCoupon, loading: removeCouponLoading } = useMutation(RemoveCouponDocument);
  async function removeCartCoupon(couponCode: string): Promise<void> {
    await _removeCoupon({ command: { couponCode, ...commonVariables }, skipQuery: false });
  }

  const { mutate: _changeComment, loading: changeCommentLoading } = useMutation(ChangeCartCommentDocument);
  async function changeComment(comment: string): Promise<void> {
    await _changeComment({ command: { comment, ...commonVariables }, skipQuery: false });
  }

  const { mutate: _changePurchaseOrderNumber, loading: changePurchaseOrderNumberLoading } = useMutation(
    ChangePurchaseOrderNumberDocument,
  );
  async function updatePurchaseOrderNumber(purchaseOrderNumber: string): Promise<void> {
    await _changePurchaseOrderNumber({ command: { purchaseOrderNumber, ...commonVariables }, skipQuery: false });
  }

  // OURS
  // convert cart mutation
  const { mutate: _convertCart, loading: convertCartLoading } = useConvertCartMutation(cart);
  async function convertCartThreading(convertCart: ConvertCartType): Promise<CartExtension> {
    const result = await _convertCart({ command: convertCart });
    return result?.data?.convertCart as CartExtension;
  }

  const { mutate: _reorderCartItems } = useReorderCartItemsMutation(cart);
  async function reorderCartItems(params: {
    lineItemIds: string[];
    previousLineItemId?: string;
    nextLineItemId?: string;
  }): Promise<void> {
    try {
      await _reorderCartItems({ command: params });
    } catch (err) {
      Logger.error("reorderCartItems failed", err);
    }
  }

  const { mutate: _updateAccessories } = useUpdateAccessoryMutation(cart);
  async function updateAccessories(accessories: InputUpdateAccessoryType): Promise<CartExtension> {
    const result = await _updateAccessories({ command: accessories });
    return result?.data?.updateAccessory as CartExtension;
  }
  // END OURS

  const {
    mutate: _addOrUpdateShipment,
    loading: addOrUpdateShipmentLoading,
    onDone: onAddOrUpdateShipmentDone,
  } = useMutation(AddOrUpdateCartShipmentDocument);

  onAddOrUpdateShipmentDone(() => resolveClient().cache.gc());

  async function updateShipment(value: InputShipmentTypeExtension): Promise<void> {
    const response = await _addOrUpdateShipment(
      { command: { shipment: value, ...commonVariables }, skipQuery: false },
      {
        optimisticResponse: (vars, { IGNORE }) => {
          if ((vars as AddOrUpdateCartShipmentMutationVariables).command.shipment.id === undefined) {
            return IGNORE as AddOrUpdateCartShipmentMutation;
          }
          return {
            addOrUpdateCartShipment: merge({}, cart.value!, {
              shipments: [
                {
                  id: value.id,
                  shipmentMethodCode: value.shipmentMethodCode,
                  shipmentMethodOption: value.shipmentMethodOption,
                  shippingAccountNumberTL: value.shippingAccountNumberTL,
                  deliveryAddress: generateCacheIdIfNew(value.deliveryAddress, "CartAddressType"),
                  comment: value.comment,
                },
              ],
            }),
          };
        },
      },
    );

    setUpdatedCart(response?.data?.addOrUpdateCartShipment as CartExtension);
  }

  const {
    mutate: _removeShipment,
    loading: removeShipmentLoading,
    onDone: onRemoveShipmentDone,
  } = useMutation(RemoveShipmentDocument);

  onRemoveShipmentDone(() => resolveClient().cache.gc());

  async function removeShipment(shipmentId: string): Promise<void> {
    await _removeShipment(
      { command: { shipmentId, ...commonVariables }, skipQuery: false },
      {
        optimisticResponse: {
          removeShipment: {
            ...cart.value!,
            shipments: cart.value!.shipments.filter((x) => x.id !== shipmentId),
          },
        },
      },
    );
  }

  const {
    mutate: _addOrUpdatePayment,
    loading: addOrUpdatePaymentLoading,
    onDone: onAddOrUpdatePaymentDone,
  } = useMutation(AddOrUpdateCartPaymentDocument);

  onAddOrUpdatePaymentDone(() => resolveClient().cache.gc());

  let currentPaymentUpdate: Promise<void> | null = null;

  async function updatePayment(value: InputPaymentTypeExtension): Promise<void> {
    // Wait for any in-progress update to complete
    if (currentPaymentUpdate) {
      Logger.info("updatePayment: Waiting for previous update to complete");
      await currentPaymentUpdate.catch((e) => {
        // Surfaces the (already-handled) previous failure only to flag this overlap for telemetry
        void logClientInformation({
          title: "Cart.updatePaymentPreviousUpdateFailed",
          content: `A previous updatePayment call failed while a new call was waiting for it. cartId=${cart.value?.id ?? "unknown"}, error=${e instanceof Error ? e.message : String(e)}`,
          withTelemetry: true,
          userId: userId ?? "unknown",
        }).catch(() => {});
      });
    }

    // Create a new update promise
    currentPaymentUpdate = (async () => {
      try {
        const response = await _addOrUpdatePayment({
          command: { payment: value, ...commonVariables },
          skipQuery: false,
        });
        setUpdatedCart(response?.data?.addOrUpdateCartPayment as CartExtension);
      } catch (e) {
        Logger.error(updatePayment.name, e);
        notifications.error({ text: t("pages.account.order_payment.failure.title") });
        throw e;
      } finally {
        currentPaymentUpdate = null;
      }
    })();

    await currentPaymentUpdate;
  }

  // OURS
  const { mutate: _addOrUpdatePaymentWithTokenValidation, loading: addOrUpdatePaymentWithTokenValidationLoading } =
    useAddOrUpdateCartPaymentWithTokenValidationMutation(cart);
  async function updatePaymentWithTokenValidation(
    value: InputPaymentTypeExtension,
    token: string,
    kid: string,
  ): Promise<AuthorizePaymentResultType> {
    const response = await _addOrUpdatePaymentWithTokenValidation(
      { command: { payment: value, kid: kid, token: token } },
      {
        optimisticResponse: (vars, { IGNORE }) => {
          if ((vars as AddOrUpdateCartPaymentWithTokenValidationMutationVariables).command.payment.id === undefined) {
            return IGNORE as AddOrUpdateCartPaymentWithTokenValidationMutation;
          }
          return {
            addOrUpdateCartPaymentWithTokenValidation: {
              isSuccess: true,
              errorMessage: undefined,
              cart: merge({}, cart.value!, {
                payments: [
                  {
                    id: value.id,
                    paymentGatewayCode: value.paymentGatewayCode,
                    billingAddress: generateCacheIdIfNew(value.billingAddress, "CartAddressType"),
                  },
                ],
              }),
            },
          };
        },
      },
    );

    if (response?.data?.addOrUpdateCartPaymentWithTokenValidation?.isSuccess) {
      setUpdatedCart(response?.data?.addOrUpdateCartPaymentWithTokenValidation?.cart as CartExtension);
    }

    return {
      isSuccess: response?.data?.addOrUpdateCartPaymentWithTokenValidation?.isSuccess ?? false,
      errorMessage: response?.data?.addOrUpdateCartPaymentWithTokenValidation?.errorMessage,
    };
  }
  // END OURS

  const { mutate: _addGiftItems, loading: addGiftItemsLoading } = useMutation(AddGiftItemsDocument);
  async function addGiftsToCart(giftIds: string[]): Promise<void> {
    await _addGiftItems({ command: { ids: giftIds, ...commonVariables }, skipQuery: false });
  }

  const { mutate: _rejectGiftItems, loading: rejectGiftItemsLoading } = useMutation(RejectGiftItemsDocument);
  async function removeGiftsFromCart(giftLineItemIds: string[]): Promise<void> {
    await _rejectGiftItems({ command: { ids: giftLineItemIds, ...commonVariables }, skipQuery: false });
  }

  async function toggleGift(gift: ExtendedGiftItemType): Promise<void> {
    if (gift.isAddedInCart) {
      await removeGiftsFromCart([gift.lineItemId!]);
    } else {
      await addGiftsToCart([gift.id]);
    }
  }

  function openClearCartModal() {
    openModal({
      component: ClearCartModal,
      props: {
        async onResult() {
          await clearCart();
        },
      },
    });
  }

  async function saveCartOnActionURL(
    saveCartUserId: string | undefined,
    cartNameToSave?: string,
    saveCartCurrencyCode?: string,
  ) {
    const args = {
      cartName: cartNameToSave ?? "Saved Cart",
      storeId: globals.storeId,
      userId: saveCartUserId,
      cartId: cartId ?? cart.value?.id,
      cultureName: globals.cultureName,
      currencyCode: saveCartCurrencyCode ?? currentCurrency.value.code,
    } as SaveCartType;
    return await saveCart(args);
  }

  async function clearCartByItemIds() {
    if (cart.value?.items.length) {
      const itemIds = cart.value.items.map((item) => item.id);
      await removeItems(itemIds);
    }
  }

  async function getFullCarts(
    cartExclusion: string,
    cartType: string,
    sort: string,
    cartNameToFilter: string = "",
    page?: number,
  ): Promise<CartExtensionConnection> {
    return await getCarts(<Partial<CartSearchParam>>{
      cartName: cartNameToFilter,
      cartExclusion,
      cartType,
      sort,
      page,
    });
  }

  async function removeCartById(id: string): Promise<boolean> {
    return await useRemoveCartMutation(id);
  }

  function setUpdatedCart(newCart: CartExtension) {
    checkoutCart.value = newCart;
  }

  function getItemDescription(item: LineItemTypeExtension) {
    if (item.customDescriptionTL) {
      if (item.product?.code === "Patch-Cable-CUSTOM" && item.customDescriptionTL?.indexOf("; Ships: ") !== -1) {
        return item.customDescriptionTL?.substring(0, item.customDescriptionTL.indexOf("; Ships: "));
      }
      return item.customDescriptionTL;
    }
    if (item.product?.mainProductId !== undefined && item.product?.mainProductId?.length > 0) {
      return (
        item.product?.masterVariation?.descriptionsWithFallbacksTL?.find((d) => d.reviewType === "Title")?.content ?? ""
      );
    } else {
      return item.product?.descriptionsWithFallbacksTL?.find((d) => d.reviewType === "Title")?.content ?? "";
    }
  }

  // Add bulk items to cart section
  const { mutate: _addBulkItemsToCart, loading: addBulkItemsToCartLoading } = useMutation(AddBulkItemsCartDocument);
  const updateCartItemDynamicProperties = useUpdateCartItemDynamicPropertiesMutation(cart);
  async function addBulkItemsToCart(
    items: InputNewBulkItemType[],
    specificCartId: string = "",
  ): Promise<OutputBulkItemType[]> {
    const {
      isFromAlgoliaSearch,
      currentAlgoliaQueryID,
      currentAlgoliaQueryIndex,
      algoliaSendAddedToCartObjectIDsAfterSearch,
    } = useThorlabsAlgoliaAnalytics();

    //Remember existing line items so we can get only new ones later
    const existingLineItemsIDs = cart.value?.items.map((item) => item.id) ?? [];

    const result = await _addBulkItemsToCart(
      {
        command: {
          cartItems: items,
          ...(specificCartId && { cartId: specificCartId }), // 👈 updated here
          ...commonVariables,
        },
      },
      {
        update: (cache, { data }) => {
          if (data?.addBulkItemsCart?.cart) {
            cache.writeQuery({
              query: GetFullCartDocument,
              data: { cart: data.addBulkItemsCart.cart },
              variables: commonVariables,
            });
          }
        },
      },
    );

    //Store Algolia queryID and index name as dynamic properties on newly added line items
    const newLineItems = (result?.data?.addBulkItemsCart?.cart?.items.filter(
      (item) => !existingLineItemsIDs.includes(item.id),
    ) ?? []) as LineItemTypeExtension[] | undefined;
    if (!!newLineItems && newLineItems.length > 0) {
      if (isFromAlgoliaSearch.value) {
        newLineItems.forEach(async (item) => {
          await updateCartItemDynamicProperties.mutate({
            command: {
              lineItemId: item.id,
              dynamicProperties: [
                {
                  name: algoliaDynamicPropertyName,
                  value: `${currentAlgoliaQueryID.value}~${currentAlgoliaQueryIndex.value}`,
                },
              ],
            },
          });
        });

        await algoliaSendAddedToCartObjectIDsAfterSearch(newLineItems);
      }

      //Trigger GTM add to cart event
      gtmSendAddToCartEvent(newLineItems);
    }

    const cartFragment = result?.data?.addBulkItemsCart?.cart;

    return items.map<OutputBulkItemType>(({ productSku, quantity }) => ({
      productSku,
      quantity,
      errors: result?.data?.addBulkItemsCart?.errors?.filter((error) => error.objectId === productSku),
      // Workaround as we don't know product IDs on bulk order
      isAddedToCart: cartFragment?.items.some(
        (item) =>
          item.sku === productSku &&
          !cartFragment?.validationErrors.some(
            (error) =>
              error.objectType == ValidationErrorObjectType.CatalogProduct && error.objectId === item.productId,
          ),
      ),
    }));
  }

  return {
    cart,
    shipment,
    payment,
    availableShippingMethods,
    availablePaymentMethods,
    selectedItemIds,
    lineItemsGroupedByVendor,
    selectedLineItems,
    selectedLineItemsGroupedByVendor,
    hasOnlyUnselectedLineItems,
    allItemsAreDigital,
    addedGiftsByIds,
    availableExtendedGifts,
    hasValidationErrors,
    hasOnlyUnselectedValidationError,
    load,
    refetch,
    forceFetch,
    setUpdatedCart,
    changeItemQuantity,
    changeItemQuantityBatched,
    changeItemQuantityBatchedOverflowed,
    // selectCartItems, NOT NEEDED
    // unselectCartItems, NOT NEEDED
    // selectionOverflowed, NOT NEEDED
    removeItems,
    validateCartCoupon,
    addCartCoupon,
    removeCartCoupon,
    changeComment,
    updateShipment,
    removeShipment,
    updatePayment,
    updatePaymentWithTokenValidation, // OURS
    updatePurchaseOrderNumber,
    clearCart,
    addGiftsToCart,
    removeGiftsFromCart,
    toggleGift,
    openClearCartModal,
    loading: readonly(loading),
    // OURS
    cartWeight,
    cartHasUkHeavyItems,
    cartHasVaporCells,
    convertCartThreading,
    reorderCartItems,
    updateAccessories,
    availabilityItems,
    cartHasDiscounts: computed(() => hasDiscounts.value),
    cartHasOnlyNewLabDiscount: computed(() => hasOnlyNewLabDiscount.value),
    getFullCarts,
    getMetricReplacementItemIds,
    itemsInCart,
    itemsInCartWithLines,
    removeCartById,
    saveCartOnActionURL,
    clearCartByItemIds,
    lastRemovedLineItem,
    lastAddedItemNumber,
    getItemDescription,
    addBulkItemsToCart,
    refreshCartItemsPrice,
    zeroPriceLineItemIds,
    // END OURS
    changing: computed(
      () =>
        selectCartItemsLoading.value ||
        unselectCartItemsLoading.value ||
        clearCartLoading.value ||
        removeItemsLoading.value ||
        changeItemQuantityLoading.value ||
        changeItemsQuantityLoading.value ||
        // validateCouponLoading.value ||
        addCouponLoading.value ||
        removeCouponLoading.value ||
        changeCommentLoading.value ||
        changePurchaseOrderNumberLoading.value ||
        addOrUpdateShipmentLoading.value ||
        removeShipmentLoading.value ||
        addOrUpdatePaymentLoading.value ||
        addOrUpdatePaymentWithTokenValidationLoading.value ||
        addGiftItemsLoading.value ||
        rejectGiftItemsLoading.value ||
        convertCartLoading.value ||
        addBulkItemsToCartLoading.value ||
        refreshCartItemsPriceLoading.value,
    ),
  };
}

const useFullCartShared = createSharedComposableByArgs(_useFullCart, (args) => `${args?.[0] ?? ""}-${args?.[1] ?? ""}`);
// export function useFullCart() {
//   const route = useRoute();
//   const cartId = Array.isArray(route.params?.cartId) ? route.params?.cartId[0] : route.params?.cartId;

//   return useFullCartShared(cartId);
// }

export function useFullCart(cartId?: string, cartName?: string) {
  return useFullCartShared(cartId, cartName);
}
